import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { ProjectMode, ProjectStatus, ProjectSettings, defaultProjectSettings } from '@/types/project';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import { TOAST } from '@/lib/toastMessages';

// Map frontend status types to database values
const STATUS_TO_DB: Record<ProjectStatus, string> = {
  'IDEA': 'planning',
  'RESEARCHING': 'research',
  'OUTLINING': 'outlining',
  'DRAFTING': 'drafting',
  'EDITING': 'revising',
  'COMPLETE': 'completed',
};

// Map database values back to frontend status types
const DB_TO_STATUS: Record<string, ProjectStatus> = {
  'planning': 'IDEA',
  'research': 'RESEARCHING',
  'outlining': 'OUTLINING',
  'drafting': 'DRAFTING',
  'revising': 'EDITING',
  'completed': 'COMPLETE',
};

export interface Project {
  id: string;
  title: string;
  description: string | null;
  status: ProjectStatus;
  mode: ProjectMode;
  genre: string | null;
  target_audience: string | null;
  settings: ProjectSettings;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateProjectData {
  title: string;
  description?: string;
  status?: ProjectStatus;
  mode?: ProjectMode;
  genre?: string;
  target_audience?: string;
  settings?: Partial<ProjectSettings>;
}

function parseSettings(settings: Json | null): ProjectSettings {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return defaultProjectSettings;
  }
  return {
    ...defaultProjectSettings,
    ...(settings as unknown as Partial<ProjectSettings>),
  };
}

async function fetchProjectsFromDb(userId: string): Promise<Project[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) throw error;

  return (data || []).map((p) => ({
    ...p,
    status: DB_TO_STATUS[p.status] || 'IDEA',
    mode: (p.mode?.toUpperCase() || 'COPILOT') as ProjectMode,
    settings: parseSettings(p.settings),
  }));
}

export function useProjects() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['projects', user?.id];

  const { data: projects = [], isLoading: loading, error } = useQuery({
    queryKey,
    queryFn: () => fetchProjectsFromDb(user!.id),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Subscribe to realtime updates for cross-tab sync
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`projects-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, queryKey]);

  const createProjectMutation = useMutation({
    mutationFn: async (data: CreateProjectData) => {
      if (!user) throw new Error('Not authenticated');

      const newProject = {
        title: data.title,
        description: data.description || null,
        status: STATUS_TO_DB[data.status || 'IDEA'], // Convert to DB format
        mode: (data.mode || 'COPILOT').toLowerCase(), // Database expects lowercase
        genre: data.genre || null,
        target_audience: data.target_audience || null,
        settings: { ...defaultProjectSettings, ...data.settings } as unknown as Json,
        user_id: user.id,
      };

      const { data: created, error } = await supabase
        .from('projects')
        .insert(newProject)
        .select()
        .single();

      if (error) throw error;

      return {
        ...created,
        status: DB_TO_STATUS[created.status] || 'IDEA',
        mode: (created.mode?.toUpperCase() || 'COPILOT') as ProjectMode,
        settings: parseSettings(created.settings),
      } as Project;
    },
    onSuccess: (newProject) => {
      queryClient.setQueryData<Project[]>(queryKey, (old = []) => [newProject, ...old]);
      toast.success(TOAST.PROJECT.CREATE_SUCCESS.title);
    },
    onError: (err) => {
      console.error('Error creating project:', err);
      toast.error(err instanceof Error ? err.message : TOAST.PROJECT.CREATE_ERROR.description);
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateProjectData> }) => {
      const updateData: Record<string, unknown> = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      // Convert status to DB format if provided
      if (updates.status) {
        updateData.status = STATUS_TO_DB[updates.status];
      }

      if (updates.settings) {
        updateData.settings = updates.settings as unknown as Json;
      }

      const { error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      return { id, updates };
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousProjects = queryClient.getQueryData<Project[]>(queryKey);

      queryClient.setQueryData<Project[]>(queryKey, (old = []) =>
        old.map((p) =>
          p.id === id
            ? { ...p, ...updates, updated_at: new Date().toISOString() } as Project
            : p
        )
      );

      return { previousProjects };
    },
    onError: (err, _, context) => {
      console.error('Error updating project:', err);
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKey, context.previousProjects);
      }
      toast.error(err instanceof Error ? err.message : TOAST.PROJECT.UPDATE_ERROR.description);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previousProjects = queryClient.getQueryData<Project[]>(queryKey);

      queryClient.setQueryData<Project[]>(queryKey, (old = []) =>
        old.filter((p) => p.id !== id)
      );

      return { previousProjects };
    },
    onSuccess: () => {
      toast.success(TOAST.PROJECT.DELETE_SUCCESS.title);
    },
    onError: (err, _, context) => {
      console.error('Error deleting project:', err);
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKey, context.previousProjects);
      }
      toast.error(err instanceof Error ? err.message : TOAST.PROJECT.DELETE_ERROR.description);
    },
  });

  const getProject = useCallback(async (id: string): Promise<Project | null> => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return {
        ...data,
        status: DB_TO_STATUS[data.status] || 'IDEA',
        mode: (data.mode?.toUpperCase() || 'COPILOT') as ProjectMode,
        settings: parseSettings(data.settings),
      };
    } catch (err) {
      console.error('Error fetching project:', err);
      return null;
    }
  }, []);

  // Wrapper functions to maintain existing API - memoized to prevent infinite loops
  const createProject = useCallback(async (data: CreateProjectData): Promise<Project | null> => {
    try {
      return await createProjectMutation.mutateAsync(data);
    } catch {
      return null;
    }
  }, [createProjectMutation]);

  const updateProject = useCallback(async (id: string, updates: Partial<CreateProjectData>): Promise<boolean> => {
    try {
      await updateProjectMutation.mutateAsync({ id, updates });
      return true;
    } catch {
      return false;
    }
  }, [updateProjectMutation]);

  const deleteProject = useCallback(async (id: string): Promise<boolean> => {
    try {
      await deleteProjectMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  }, [deleteProjectMutation]);

  return {
    projects,
    loading,
    error: error?.message || null,
    createProject,
    updateProject,
    deleteProject,
    getProject,
    refetch: () => queryClient.invalidateQueries({ queryKey }),
  };
}
